import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { Component, OnDestroy, OnInit } from '@angular/core';

import { ContextMenuOption } from '../../../../shared/bb-context-menu/bb-context-menu.component';
import { bbGenerateShortId } from '../../../../../shared/utils/bbGenerateShortId.util';
import { bbDeepCopy } from '../../../../../shared/utils/bbDeepCopy.util';

import { InteractivityWidgetSelectionService } from '../../services/interactivity-widget-selection.service';
import { InteractivityEditorService } from '../../services/interactivity-editor.service';
import { IWidgetSelection } from '../../interfaces/IWidgetSelection';
import { IWidget } from '../../interfaces/IWidget';
import { IProcedure, ITimelineForm, IMessage } from '../../redux/timeline-data.model';
import { StageWidget } from '../../classes/StageWidget';
import { Constants } from '../../classes/constants';

@Component({
	selector: 'inspector',
	templateUrl: './inspector.component.html',
	styleUrls: ['./inspector.component.scss']
})

export class InspectorComponent implements OnInit, OnDestroy {
	readonly propsNotToRender: string[] = ['publication'];
	public eventHandlerNames: string[];

	propertiesOfSelectedWidgets: any[];
	messages: IMessage[];
	widgetSelection: IWidgetSelection;
	singleSelectedWidgetId: string;
	selectedWidget: IWidget;
	selectedWidgetTags: any[] = [];
	currentTab = 'actions';
	newMessageOptions: any[] = [];
	newMessageModel = '';
	contextMenuStyle: any = { display: 'none' };
	stageWidgets: IWidget[];
	stageWidgetsWithKPI: IWidget[];
	visibleWidgets: IWidget[];
	nonStageWidgetsSelected: boolean;
	previousSelectedWidgetIds = '';

	contextMenuOptions: any[] = [];
	private forms: ITimelineForm[] = [];
	private subscriptions = new Subscription();

	constructor(
		private interactivityWidgetSelectionService: InteractivityWidgetSelectionService,
		private interactivityEditorService: InteractivityEditorService,
		private translate: TranslateService
	) {
		this.eventHandlerNames = Constants.eventHandlerNames;
	}

	static cloneProcedure(procedure: IProcedure): IProcedure {
		let clone: IProcedure = void (0);
		if (procedure !== undefined) {
			clone = bbDeepCopy(procedure);
			if (clone && clone.actions) {
				for (let i = 0, l = clone.actions.length; i < l; i++) {
					const action = clone.actions[i];
					action.id = bbGenerateShortId();
				}
			}
			if (clone && clone.else_actions) {
				for (let i = 0, l = clone.else_actions.length; i < l; i++) {
					const else_action = clone.else_actions[i];
					else_action.id = bbGenerateShortId();
				}
			}
		}

		return clone;
	}

	ngOnInit() {
		this.subscriptions.add(this.interactivityEditorService.formsSubject.subscribe((formsData: any) => {
			this.forms = formsData;
		}));

		this.subscriptions.add(
			this.interactivityWidgetSelectionService.widgetSelectionSubject.subscribe((data: IWidgetSelection) => {
				// this.interactivityActionService.clearActionDropSites(); // clear action drop sites. Rendering new from here on
				this.widgetSelection = data;
				this.propertiesOfSelectedWidgets = data.commonProperties;

				if (data.widgets && data.widgets.length === 1) {
					// single selected widget
					this.singleSelectedWidgetId = data.widgets[0].id;
					this.selectedWidget = data.widgets[0];
				} else {
					this.singleSelectedWidgetId = null;
					this.selectedWidget = null;

					this.stageWidgets = data.widgets.filter((widget) => widget.type !== 'app' && widget.type !== 'timer' && widget.type !== 'audio');
					this.stageWidgetsWithKPI = this.stageWidgets.filter((widget) => widget.getConfigValue('useAsKPI') === 'true');

					this.visibleWidgets = this.stageWidgets.filter((widget) => widget instanceof StageWidget && widget.visibility === 'visible');

					this.nonStageWidgetsSelected = data.widgets && data.widgets.length > this.stageWidgets.length;
				}
				this.selectedWidgetTags = this.selectedWidget && this.selectedWidget.grouping ? this.selectedWidget.grouping : [];
				if (data.widgets && data.widgets.length > 0) {
					const selectedWidgetIds = JSON.stringify(data.widgets.map((widget) => widget.id));
					if (selectedWidgetIds !== this.previousSelectedWidgetIds) {
						this.setTab('actions');
					}
					this.previousSelectedWidgetIds = selectedWidgetIds;
				}
			}));

		this.subscriptions.add(
			this.interactivityEditorService.messagesSubject.subscribe((messagesData: IMessage[]) => {
				this.messages = messagesData;
			}));

		this.subscriptions.add(this.interactivityEditorService.widgetVisibilitySubject.subscribe(() => {
			this.stageWidgets = this.interactivityWidgetSelectionService.widgetSelection.widgets
				.filter((widget) => widget.type !== 'app' && widget.type !== 'timer' && widget.type !== 'audio');

			this.visibleWidgets = this.stageWidgets.filter((widget) => widget instanceof StageWidget && widget.visibility === 'visible');
		}));
	}

	ngOnDestroy() {
		this.subscriptions.unsubscribe();
	}

	isStageWidget(widget: IWidget): boolean {
		return (widget instanceof StageWidget);
	}

	setTab(tab: string) {
		this.currentTab = tab;

		this.newMessageOptions = this.interactivityEditorService.messageOptions.map(v => ({ label: v, value: v }));
		// unused: 'ready seeking suspend stalled unstalled abort'
		('started play playing pause ended loadedclipdata modechange phasechange statechange durationchange canplay resized error timeupdate' +
			' seeked loadedmetadata inview outview assetselected loadeddata').split(' ').forEach((eventName) => {
			this.newMessageOptions.push({
				label: this.translate.instant('PLAYER_' + eventName.toUpperCase()),
				value: 'player_' + eventName
			});
		});
	}

	eventHandlerTrackBy(i: number) {
		return this.singleSelectedWidgetId + '_eventhandler_' + i;
	}

	messageTrackBy(i: number, item: any) {
		return item.name + '_eventhandler_' + i;
	}

	propertyChanged(obj: any) {
		if (obj.key !== null && obj.value !== null) {
			this.changeProperty(obj.key, obj.value, obj.msg);
		}
	}

	onNewMessageSelected(messageName: string) {
		this.interactivityEditorService.addMessage(messageName);

		setTimeout(() => {
			this.newMessageModel = '';
		});
	}

	messageChanged(obj: any) {
		if (obj.key !== null && obj.value !== null) {
			this.changeMessageProperty(obj.key, obj.value, obj.msg);
		}
	}

	messageNameChanged(ev: any) {
		if (ev.oldValue && ev.newValue && ev.oldValue !== ev.newValue) {
			this.interactivityEditorService.renameMessage(ev.oldValue, ev.newValue, `Renamed message handler from ${ev.oldValue} to ${ev.newValue}`);
		}
	}

	removeMessage(messageName: string) {
		if (messageName) {
			this.interactivityEditorService.removeMessage(messageName, 'Removed message: ' + messageName);
		}
	}

	changeProperty(key: string, val: any, msg: string): void {
		this.interactivityEditorService.changeWidgetsProperty([this.selectedWidget], key, val, msg);
	}

	changeFormProperty(key: string, val: any, msg: string): void {
		this.interactivityEditorService.changeFormField(key, val, msg);
	}

	changeMessageProperty(key: string, val: any, msg: string) {
		this.interactivityEditorService.changeMessage(key, val, msg);
	}

	onMessageEnterKey(ev: Event, key: string, val: any) {
		if (key && val) {
			// TODO
			// this.changeMessageProperty(key, val, `change widget property: ${key}` + typeof val === 'string' ? ' to ' + val : '');
		}
	}

	onEnterKey(ev: Event, key: string, val: any) {
		if (key && val) {
			this.changeProperty(key, val, `change widget property: ${key}` + typeof val === 'string' ? ' to ' + val : '');
		}
	}

	toggleWidgetKPI(): void {
		if (this.selectedWidget) {
			if (this.selectedWidget.getConfigValue('useAsKPI') === 'false') {
				this.interactivityEditorService.changeWidgetsProperty([this.selectedWidget], 'useAsKPI', 'true');
			} else {
				this.interactivityEditorService.changeWidgetsProperty([this.selectedWidget], 'useAsKPI', 'false');
			}
		}
	}

	setWidgetsKPI(on: boolean) {
		if (this.widgetSelection && this.widgetSelection.widgets && this.widgetSelection.widgets.length) {
			this.interactivityEditorService.changeWidgetsProperty(this.widgetSelection.widgets, 'useAsKPI', (on ? 'true' : 'false'));
		}
	}

	toggleWidgetVisibility(widget: StageWidget) {
		this.interactivityEditorService.toggleWidgetVisibility(widget);
	}

	setWidgetsVisibility(widgets: IWidget[], visible: boolean) {
		this.interactivityEditorService.toggleWidgetVisibilityMulti(widgets, visible);
	}

	onEventHandlerContextMenu(ev: any, key: string, index: number, item: any): void {
		if (ev) {
			ev.preventDefault();
			ev.stopPropagation();
		}

		let selectedProcedures: any[] = []; // IProcedure[]?
		if (item && item.value && item.value instanceof Array) {
			selectedProcedures = item.value;
		}

		this.contextMenuOptions = [];
		// Copy
		if (item && (item.actions !== undefined)) { // implements IProcedure
			const property = { key, index, value: item };
			this.contextMenuOptions.push({
				item: property,
				label: 'Copy procedure',
				value: 'copy',
				icon: 'copy'
			});
		} else if (selectedProcedures && selectedProcedures.length > 0) { // selection
			this.contextMenuOptions.push({
				item,
				label: 'Copy ' + selectedProcedures.length + ' procedures',
				value: 'copy',
				icon: 'copy'
			});
		}

		// Paste
		const procedures = this.interactivityEditorService.clipboardGet('procedures');
		if (procedures && procedures.length > 0) {
			if (item && (item.actions !== undefined)) { // implements IProcedure
				const property = { key, index, value: item };
				this.contextMenuOptions.push({
					item: property,
					label: 'Paste ' + procedures.length + ' procedures',
					value: 'paste',
					icon: 'paste'
				});
			} else { // selection
				this.contextMenuOptions.push({
					item,
					label: 'Paste ' + procedures.length + ' procedures',
					value: 'paste',
					icon: 'paste'
				});
			}
		}

		// Duplicate
		if (item && (item.actions !== undefined)) { // implements IProcedure
			const property = { key, index, value: item };
			this.contextMenuOptions.push({
				item: property,
				label: 'Duplicate procedure',
				value: 'duplicate',
				icon: 'copy'
			});
		} else if (selectedProcedures && selectedProcedures.length > 0) { // selection
			this.contextMenuOptions.push({
				item,
				label: `Duplicate ${selectedProcedures.length} procedures`,
				value: 'duplicate',
				icon: 'copy'
			});
		}

		// Cut
		if (item && (item.actions !== undefined)) { // implements IProcedure
			const property = { key, index, value: item };
			this.contextMenuOptions.push({
				item: property,
				label: 'Cut procedure',
				value: 'cut',
				icon: 'scissors'
			});
		} else if (selectedProcedures && selectedProcedures.length > 0) { // selection
			this.contextMenuOptions.push({
				item,
				label: 'Cut ' + selectedProcedures.length + ' procedures',
				value: 'cut',
				icon: 'scissors'
			});
		}

		// Delete
		if (item && (item.actions !== undefined)) {
			const property = { key, index, value: item };
			this.contextMenuOptions.push({
				item: property,
				label: 'Delete procedure',
				value: 'delete',
				icon: 'trash'
			});
		} else if (selectedProcedures && selectedProcedures.length > 0) { // selection
			this.contextMenuOptions.push({
				item,
				label: 'Delete ' + selectedProcedures.length + ' procedures',
				value: 'delete',
				icon: 'trash'
			});
		}

		// Delete Message
		if (item.type && item.type === 'message') {
			this.contextMenuOptions.push({
				item: item.key,
				label: 'Delete Message',
				value: 'deleteMessage',
				icon: 'trash'
			});
		}

		if (this.contextMenuOptions.length > 0) {
			this.contextMenuShow(ev.pageX, ev.pageY);
		}
	}

	onProcedureContextMenu(ev: any, key: string, index: number, item: any): void {
		if (ev) {
			this.onEventHandlerContextMenu(ev, key, index, item);
		}
	}

	onContextMenuOptionSelected(option: ContextMenuOption): void {
		switch (option.value) {
			case 'copy':
				if (option.item.value instanceof Array) {
					this.interactivityEditorService.clipboardSet(option.item.value, 'procedures');
				} else if (option.item.value) { // instanceof Object) { // IProcedure
					this.interactivityEditorService.clipboardSet([option.item.value], 'procedures');
				}
				break;

			case 'paste':
				const value = this.interactivityEditorService.clipboardGet('procedures');
				if (value instanceof Array) {
					this.duplicateProcedures(value, option.item.key, option.item.index);
				} else if (value) { // instanceof Object) { // IProcedure
					this.duplicateProcedures([value], option.item.key);
				}
				break;

			case 'duplicate':
				if (option.item.value instanceof Array) {
					this.duplicateProcedures(option.item.value, option.item.key, option.item.index);
				} else if (option.item.value) { // instanceof Object) { // IProcedure
					this.duplicateProcedures([option.item.value], option.item.key);
				}
				break;

			case 'cut':
				if (option.item.value instanceof Array) {
					this.interactivityEditorService.clipboardSet(option.item.value, 'procedures');
				} else if (option.item.value) { // instanceof Object) { // IProcedure
					this.interactivityEditorService.clipboardSet([option.item.value], 'procedures');
				}
			// eslint-disable-next-line no-fallthrough
			case 'delete':
				if (option.item.value instanceof Array) {
					this.deleteProcedures(option.item.value, option.item.key);
				} else if (option.item.value) { // instanceof Object) { // IProcedure
					this.deleteProcedures([option.item.value], option.item.key);
				}
				break;
			case 'deleteMessage':
				if (option.item) {
					this.removeMessage(option.item);
				}
				break;
		}
		this.contextMenuHide();
	}

	contextMenuHide(): void {
		this.contextMenuStyle.display = 'none';
	}

	private contextMenuShow(posX: number, posY: number): void {
		this.contextMenuStyle.display = 'block';
		this.contextMenuStyle.left = posX + 'px';
		this.contextMenuStyle.top = posY + 'px';
	}

	private determineProperty(key: string): any {
		let property: any;

		// check the current tab, get current state of targeted event handler and make sure the output is in the same format

		if (this.propertiesOfSelectedWidgets && this.propertiesOfSelectedWidgets.length > 0 && this.currentTab === 'actions') {
			property = this.propertiesOfSelectedWidgets.find(prop => prop && prop.key === key);
		} else if (this.forms && this.forms[0][key] && this.currentTab === 'form') {
			property = { key, value: this.forms[0][key] };
		} else if (this.messages && this.messages.length > 0 && this.currentTab === 'messages') {
			const foundMessage = this.messages.find(message => message && message.name === key);
			if (foundMessage && foundMessage.procedures) {
				property = { key, value: foundMessage.procedures };
			}
		}
		return property;
	}

	private duplicateProcedures(procedures: any[], key: string, index: number = -1) {
		const property = this.determineProperty(key);

		if (property && procedures) {
			const value = property.value || [];
			for (let i = 0, l = procedures.length; i < l; i++) {
				if (index >= 0) {
					value.splice(index, 0, InspectorComponent.cloneProcedure(procedures[i]));
					index++;
				} else {
					value.push(InspectorComponent.cloneProcedure(procedures[i]));
				}
			}
			switch (this.currentTab) {
				case 'form':
					this.changeFormProperty(key, value, 'extra procedures');
					break;
				case 'messages':
					this.changeMessageProperty(key, value, 'extra procedures');
					break;
				default:
					this.changeProperty(key, value, 'extra procedures');
					break;
			}
		}
	}

	private deleteProcedures(procedures: any[], key: string) {
		const property = this.determineProperty(key);

		if (property && procedures) {
			const value = (property.value) ? property.value.filter((proc: any) => proc && procedures.indexOf(proc) < 0) : null;
			switch (this.currentTab) {
				case 'form':
					this.changeFormProperty(key, value, 'extra procedures');
					break;
				case 'messages':
					this.changeMessageProperty(key, value, 'extra procedures');
					break;
				default:
					this.changeProperty(key, value, 'extra procedures');
					break;
			}
		}
	}
}
